// @figma file=pkCGby54zXzTsccXvHqzO6 node=tbd name="VersionBumpSelector" set="mytaible-shadcn-studio"

import type * as React from 'react';
import { cn } from '../../../lib/utils';
import { Switch } from './switch';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface BumpOption {
  value: string;
  label: string;
  description: string;
  /** Optional version string shown in monospace at the bottom of the card */
  preview?: string;
}

export interface VersionBumpSelectorProps {
  /** Selectable bump options rendered as cards */
  options: BumpOption[];
  /** Currently selected option value */
  value: string;
  onValueChange: (value: string) => void;
  /** Controls the beta toggle state */
  beta?: boolean;
  /** When provided, renders the beta toggle row below the cards */
  onBetaChange?: (beta: boolean) => void;
  betaLabel?: string;
  betaDescription?: React.ReactNode;
}

// ─── VersionBumpSelector ──────────────────────────────────────────────────────

const GRID_COLS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
};

export function VersionBumpSelector({
  options,
  value,
  onValueChange,
  beta = false,
  onBetaChange,
  betaLabel = 'Beta release',
  betaDescription,
}: VersionBumpSelectorProps) {
  const colsCls = GRID_COLS[Math.min(options.length, 3)] ?? 'grid-cols-3';

  return (
    <div className="space-y-2">
      <div className={cn('grid gap-2', colsCls)}>
        {options.map((opt) => {
          const selected = opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              aria-pressed={selected}
              onClick={() => onValueChange(opt.value)}
              className={cn(
                'flex flex-col gap-1 rounded-lg border p-3 text-left transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                selected
                  ? 'border-primary bg-primary/5 ring-1 ring-primary'
                  : 'border-border bg-input-bg hover:bg-muted/50',
              )}
            >
              <span
                className={cn('text-sm font-medium', selected ? 'text-primary' : 'text-foreground')}
              >
                {opt.label}
              </span>
              <span className="text-[11px] leading-tight text-muted-foreground">
                {opt.description}
              </span>
              {opt.preview !== undefined && (
                <span
                  className={cn(
                    'mt-1 font-mono text-xs',
                    selected ? 'text-primary' : 'text-muted-foreground',
                  )}
                >
                  {opt.preview}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {onBetaChange !== undefined && (
        <div className="flex items-center justify-between rounded-lg border border-border bg-input-bg px-3 py-2.5">
          <div>
            <p className="text-sm font-medium text-foreground">{betaLabel}</p>
            {betaDescription && <p className="text-xs text-muted-foreground">{betaDescription}</p>}
          </div>
          <Switch checked={beta} onCheckedChange={onBetaChange} />
        </div>
      )}
    </div>
  );
}
